import type { EventoSse } from "@copi/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { sseManager } from "./sse-manager";

import { panelEstudianteMioQueryKey } from "@/features/dashboard/panel-estudiante/use-panel-estudiante";
import { API_URL } from "@/lib/api/client";

/**
 * Hook que conecta el componente al singleton `sseManager` y mapea cada
 * evento SSE a invalidaciones de TanStack Query (ADR 018 §4).
 *
 * Cómo se usa:
 *  - Montar en el shell autenticado (`AppShell`). Si se monta varias veces,
 *    no pasa nada — el manager es singleton y la suscripción se desuscribe
 *    al desmontar.
 *  - Pasar `enabled: false` cuando no hay sesión (logout). Sin token la
 *    conexión fallaría con 401 en loop.
 *
 * **Por qué el singleton (`sseManager`) y no un `new SseConnection` por
 * efecto:** ver `sse-manager.ts`. Resumen: en desarrollo React StrictMode
 * dispara `mount → cleanup → mount` extra, y cualquier cambio sutil en
 * las deps del effect dispara cleanup+setup espurios. Con una conexión
 * dentro del efecto, cada uno de esos ciclos cerraba y reabría el SSE —
 * generando un loop de connect/disconnect cada ~500 ms en el server. El
 * manager singleton absorbe esos ciclos: la conexión vive a nivel
 * módulo y solo se cierra en logout real.
 *
 * Las queryKeys aquí listadas son **deliberadamente amplias** (prefix
 * matching de TanStack Query): un evento `expediente.estado-cambio`
 * invalida `["expedientes"]` enteros, no solo el detalle específico.
 * Esto es barato (TanStack dedupe los fetches) y robusto (no hay forma
 * de "olvidar" actualizar una vista derivada).
 */
export function useSse(options: {
  enabled: boolean;
  onRefreshNeeded?: () => Promise<string | null>;
}): void {
  const queryClient = useQueryClient();

  // Ref del callback de refresh para que cambios de identidad de función
  // no causen re-conexión (el callback se lee en el momento del 401).
  const onRefreshNeededRef = useRef(options.onRefreshNeeded);
  useEffect(() => {
    onRefreshNeededRef.current = options.onRefreshNeeded;
  }, [options.onRefreshNeeded]);

  useEffect(() => {
    if (!options.enabled) {
      // Logout o sesión perdida: cerramos la conexión global.
      sseManager.desconectar();
      return undefined;
    }

    // Asegura que el singleton esté conectado (idempotente: si ya lo está,
    // no abre otra conexión). Cualquier ciclo de StrictMode o re-render
    // espurio que ejecute este effect varias veces seguidas solo entra
    // a este branch y no hace nada — la conexión sigue viva.
    sseManager.conectar({
      baseUrl: API_URL,
      onRefreshNeeded: async () => {
        const fn = onRefreshNeededRef.current;
        return fn ? fn() : null;
      },
    });

    // Suscribimos al canal global. Cuando el componente se desmonta o el
    // effect re-corre, solo desuscribimos — la conexión global sigue
    // intacta para los demás suscriptores (y para el próximo ciclo).
    const unsubscribe = sseManager.subscribe((evento) => {
      invalidarPorEvento(evento, queryClient);
    });

    return () => {
      unsubscribe();
    };
  }, [options.enabled, queryClient]);
}

/**
 * QueryKeys de bandejas/listas que cualquier cambio en un expediente puede
 * afectar — agrupadas para invalidación masiva. TanStack Query hace
 * prefix-matching: invalidar `["decanato"]` cubre
 * `["decanato", "solicitudes-pendientes"]`, `["decanato", "expedientes", {...}]`,
 * etc.
 *
 * Invalidación amplia es **barata** (TanStack dedupe los fetches y solo
 * refetcha las que están montadas) y **robusta** (cero riesgo de olvidar
 * una vista derivada). El costo real es 0 — las bandejas que el usuario
 * actual no tiene montadas simplemente se marcan stale.
 */
const QUERY_KEYS_BANDEJAS: readonly (readonly unknown[])[] = [
  // Listas por rol — cada rol tiene su propia bandeja.
  ["decanato"],
  ["asesoria"],
  ["jurado"],
  ["repositorio"],
  // Listas transversales que filtran por estado o actor.
  ["solicitudes"],
  ["expedientes"],
  ["aceptaciones-asesor"],
  // Dashboards por rol — RT100 (post-mortem 2026-05-27).
  //
  // Bug: el `DecanatoDashboard` usa queryKey `["dashboard", "decanato"]`,
  // pero el prefix `["decanato"]` de arriba NO lo cubre (los prefixes de
  // TanStack Query son posicionales por elemento — el primer slot tiene
  // que matchear). Resultado: cuando el estudiante presentaba la
  // solicitud al Decanato, los KPIs no se refrescaban hasta recargar la
  // página. Para no andar enumerando cada `["dashboard", <rol>]`, usamos
  // el prefix raíz `["dashboard"]` — barato (los dashboards no montados
  // simplemente se marcan stale) y robusto (futuros dashboards por rol
  // quedan cubiertos sin tocar este archivo).
  ["dashboard"],
];

/**
 * Tabla central evento → queryKey. Exportada para los tests del hook.
 * Cualquier ampliación del wire (ADR 018 §3.2) requiere actualizar esta
 * función Y los tests asociados.
 *
 * Cuando un expediente cambia de estado o llega una notificación con
 * `expedienteId`, **invalidamos todas las bandejas** porque cualquier rol
 * conectado (estudiante dueño, asesor designado, decanato, jurado) puede
 * estar mirando su propia vista del expediente. Es el patrón "evento =
 * señal" del ADR 018 llevado a su conclusión natural — el cliente refetcha
 * solo lo que tiene montado.
 */
export function invalidarPorEvento(
  evento: EventoSse,
  queryClient: { invalidateQueries: (opts: { queryKey: readonly unknown[] }) => unknown },
): void {
  switch (evento.type) {
    case "ping":
    case "conectado":
      // Eventos de transporte — no impactan la UI.
      return;

    case "notificacion.nueva":
      queryClient.invalidateQueries({ queryKey: ["notificaciones", "mias"] });
      if (evento.expedienteId) {
        invalidarEcosistemaExpediente(queryClient, evento.expedienteId);
      }
      return;

    case "expediente.estado-cambio":
      invalidarEcosistemaExpediente(queryClient, evento.expedienteId);
      return;

    case "aceptacion.respondida":
      invalidarEcosistemaExpediente(queryClient, evento.expedienteId);
      return;

    case "comentario-latex.nuevo":
    case "comentario-latex.resuelto":
      // Invalida la lista de comentarios del proyecto. El componente
      // refetcha y CodeMirror re-renderiza las decoraciones con el
      // nuevo set. Se usa prefix matching: la queryKey real incluye
      // (proyectoId, filtros), pero `["comentarios-latex", proyectoId]`
      // invalida todas las variantes filtradas a la vez.
      queryClient.invalidateQueries({
        queryKey: ["comentarios-latex", evento.proyectoId],
      });
      return;
  }
}

/**
 * Invalidación canónica cuando un expediente cambió de algún modo: panel
 * del estudiante, detalle específico del expediente, y todas las
 * bandejas/listas que podrían estar mostrándolo a otros roles.
 */
function invalidarEcosistemaExpediente(
  queryClient: { invalidateQueries: (opts: { queryKey: readonly unknown[] }) => unknown },
  expedienteId: string,
): void {
  // Panel del estudiante dueño.
  queryClient.invalidateQueries({ queryKey: panelEstudianteMioQueryKey });
  // Detalle específico del expediente — útil si está abierto.
  queryClient.invalidateQueries({
    queryKey: ["expedientes", expedienteId, "detalle"],
  });
  // Jurados del expediente — ADR 027: cuando se designa un jurado por
  // tipo el panel debe reflejar la nueva composición sin recarga manual.
  queryClient.invalidateQueries({
    queryKey: ["expedientes", expedienteId, "jurados"],
  });
  // Todas las bandejas por rol y listas transversales (prefix matching).
  for (const key of QUERY_KEYS_BANDEJAS) {
    queryClient.invalidateQueries({ queryKey: key });
  }
}
